import React, { useState, useEffect } from 'react';
import { supabase } from './supabase';

export default function AdminPage({ user }) {
  const [stats, setStats] = useState(null);
  const [reported, setReported] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [tab, setTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [unauthorized, setUnauthorized] = useState(false);

  useEffect(() => {
    if (!user) { setUnauthorized(true); setLoading(false); return; }
    checkAdminAndLoad();
  }, [user]);

  async function checkAdminAndLoad() {
    setLoading(true);

    try {
      // Get all admin emails and check manually to avoid any query issues
      const { data: admins, error } = await supabase
        .from('admins')
        .select('email');

      if (error) {
        console.error('Admin check error:', error);
        setUnauthorized(true);
        setLoading(false);
        return;
      }

      const userEmail = user.email.trim().toLowerCase();
      const isAdmin = admins && admins.some(a => a.email.trim().toLowerCase() === userEmail);

      if (!isAdmin) {
        setUnauthorized(true);
        setLoading(false);
        return;
      }

      loadData();
    } catch(e) {
      console.error('Admin check exception:', e);
      setUnauthorized(true);
      setLoading(false);
    }
  }

  async function loadData() {
    setLoading(true);

    // Stats
    const { data: statsData } = await supabase.from('admin_stats').select('*').single();
    setStats(statsData);

    // Reported messages
    const { data: rep } = await supabase
      .from('messages').select('*, profiles!messages_jar_id_fkey(name)')
      .eq('reported', true)
      .order('created_at', { ascending: false });
    setReported(rep || []);

    // All users
    const { data: users } = await supabase
      .from('profiles').select('*')
      .order('created_at', { ascending: false });
    setAllUsers(users || []);

    setLoading(false);
  }

  async function deleteMessage(id) {
    await supabase.from('messages').delete().eq('id', id);
    setReported(prev => prev.filter(m => m.id !== id));
    setStats(prev => prev ? { ...prev, reported_messages: prev.reported_messages - 1, total_messages: prev.total_messages - 1 } : prev);
  }

  async function clearReport(id) {
    await supabase.from('messages').update({ reported: false, report_count: 0 }).eq('id', id);
    setReported(prev => prev.filter(m => m.id !== id));
    setStats(prev => prev ? { ...prev, reported_messages: prev.reported_messages - 1 } : prev);
  }

  if (unauthorized) return (
    <div style={styles.center}>
      <div style={{ fontSize: '3rem', marginBottom: 16 }}>🔒</div>
      <div style={{ fontSize: '1.2rem', color: '#FF6B6B' }}>Unauthorized</div>
      <div style={{ color: '#7A7090', marginTop: 8, fontSize: '0.85rem' }}>Admin access only</div>
      {user && (
        <div style={{ marginTop: 16, padding: '16px 20px', background: '#16161F', borderRadius: 10, fontSize: '0.78rem', color: '#7A7090', border: '1px solid rgba(255,209,102,0.15)', maxWidth: 360, wordBreak: 'break-all' }}>
          <div>Logged in as:</div>
          <div style={{ color: '#FFD166', marginTop: 4 }}>"{user.email}"</div>
          <div style={{ marginTop: 8 }}>Length: <span style={{ color: '#FFD166' }}>{user.email.length}</span></div>
          <div style={{ marginTop: 4 }}>Trimmed: <span style={{ color: '#FFD166' }}>"{user.email.trim()}"</span></div>
          <div style={{ marginTop: 4 }}>Lowercased: <span style={{ color: '#FFD166' }}>"{user.email.trim().toLowerCase()}"</span></div>
        </div>
      )}
      {!user && (
        <div style={{ marginTop: 16, fontSize: '0.82rem', color: '#FF8A80' }}>
          You are not logged in. Please <a href="/login" style={{ color: '#FFD166' }}>log in</a> first.
        </div>
      )}
    </div>
  );

  if (loading) return (
    <div style={styles.center}>
      <div style={{ fontSize: '2rem', color: '#FFD166' }}>✨</div>
      <div style={{ color: '#7A7090', marginTop: 12 }}>Loading admin data…</div>
    </div>
  );

  return (
    <div style={styles.page}>
      {/* Header */}
      <div style={styles.header}>
        <div>
          <div style={styles.headerTitle}>🛡️ MyGlow Admin</div>
          <div style={styles.headerSub}>myglow.me dashboard</div>
        </div>
        <button style={styles.refreshBtn} onClick={loadData}>↻ Refresh</button>
      </div>

      {/* Stats Grid */}
      <div style={styles.statsGrid}>
        {[
          { label: 'Total Users', value: stats?.total_users || 0, icon: '👥', color: '#FFD166' },
          { label: 'New This Week', value: stats?.new_users_week || 0, icon: '🌱', color: '#B9F6CA' },
          { label: 'Total Messages', value: stats?.total_messages || 0, icon: '💌', color: '#81D4FA' },
          { label: 'New This Week', value: stats?.new_messages_week || 0, icon: '✨', color: '#CE93D8' },
          { label: 'Unread Messages', value: stats?.unread_messages || 0, icon: '📬', color: '#FFCC80' },
          { label: 'Reported', value: stats?.reported_messages || 0, icon: '🚨', color: '#FF8A80' },
        ].map((s, i) => (
          <div key={i} style={styles.statCard}>
            <div style={{ fontSize: '1.5rem', marginBottom: 8 }}>{s.icon}</div>
            <div style={{ ...styles.statValue, color: s.color }}>{s.value}</div>
            <div style={styles.statLabel}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div style={styles.tabs}>
        {['overview', 'reported', 'users'].map(t => (
          <button key={t} style={{ ...styles.tab, ...(tab === t ? styles.tabActive : {}) }}
            onClick={() => setTab(t)}>
            {t === 'overview' ? '📊 Overview' : t === 'reported' ? `🚨 Reported (${reported.length})` : `👥 Users (${allUsers.length})`}
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {tab === 'overview' && (
        <div style={styles.section}>
          <div style={styles.sectionTitle}>📊 App Health</div>
          <div style={styles.healthGrid}>
            <div style={styles.healthCard}>
              <div style={styles.healthLabel}>Messages per user</div>
              <div style={styles.healthValue}>
                {stats?.total_users > 0 ? (stats.total_messages / stats.total_users).toFixed(1) : 0}
              </div>
            </div>
            <div style={styles.healthCard}>
              <div style={styles.healthLabel}>Read rate</div>
              <div style={styles.healthValue}>
                {stats?.total_messages > 0
                  ? `${Math.round(((stats.total_messages - stats.unread_messages) / stats.total_messages) * 100)}%`
                  : '0%'}
              </div>
            </div>
            <div style={styles.healthCard}>
              <div style={styles.healthLabel}>Report rate</div>
              <div style={{ ...styles.healthValue, color: stats?.reported_messages > 0 ? '#FF8A80' : '#B9F6CA' }}>
                {stats?.total_messages > 0
                  ? `${((stats.reported_messages / stats.total_messages) * 100).toFixed(2)}%`
                  : '0%'}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reported Tab */}
      {tab === 'reported' && (
        <div style={styles.section}>
          <div style={styles.sectionTitle}>🚨 Reported Messages</div>
          {reported.length === 0 ? (
            <div style={styles.empty}>
              <div style={{ fontSize: '2.5rem', marginBottom: 12 }}>✅</div>
              <div>No reported messages — all clear!</div>
            </div>
          ) : (
            <div style={styles.list}>
              {reported.map(msg => (
                <div key={msg.id} style={styles.reportCard}>
                  <div style={styles.reportMeta}>
                    <span style={styles.reportEmoji}>{msg.emoji}</span>
                    <span style={styles.reportTo}>To: {msg.profiles?.name || 'Unknown'}</span>
                    <span style={styles.reportCount}>🚨 {msg.report_count} report{msg.report_count !== 1 ? 's' : ''}</span>
                    <span style={styles.reportTime}>{new Date(msg.created_at).toLocaleDateString()}</span>
                  </div>
                  <div style={styles.reportText}>"{msg.text}"</div>
                  <div style={styles.reportActions}>
                    <button style={styles.btnDanger} onClick={() => deleteMessage(msg.id)}>🗑️ Delete</button>
                    <button style={styles.btnSafe} onClick={() => clearReport(msg.id)}>✅ Mark Safe</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Users Tab */}
      {tab === 'users' && (
        <div style={styles.section}>
          <div style={styles.sectionTitle}>👥 All Users ({allUsers.length})</div>
          <div style={styles.list}>
            {allUsers.map((u, i) => (
              <div key={u.id} style={styles.userRow}>
                <div style={styles.userNum}>#{i + 1}</div>
                <div style={styles.userAvatar}>{u.name[0].toUpperCase()}</div>
                <div style={styles.userInfo}>
                  <div style={styles.userName}>{u.name}</div>
                  <div style={styles.userMeta}>myglow.me/{u.jar_id} · joined {new Date(u.created_at).toLocaleDateString()}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  page: { maxWidth: 900, margin: '0 auto', padding: '32px 20px', fontFamily: "'Outfit', sans-serif", color: '#F0EAD6' },
  center: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '80vh', color: '#F0EAD6', fontFamily: "'Outfit', sans-serif" },
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 32, flexWrap: 'wrap', gap: 12 },
  headerTitle: { fontSize: '1.6rem', fontWeight: 700, color: '#FFD166' },
  headerSub: { fontSize: '0.8rem', color: '#7A7090', marginTop: 2 },
  refreshBtn: { padding: '8px 16px', borderRadius: 8, background: 'rgba(255,209,102,0.12)', border: '1px solid rgba(255,209,102,0.25)', color: '#FFD166', cursor: 'pointer', fontSize: '0.85rem', fontFamily: 'inherit' },
  statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 12, marginBottom: 28 },
  statCard: { background: '#16161F', border: '1px solid rgba(255,209,102,0.12)', borderRadius: 16, padding: '20px 16px', textAlign: 'center' },
  statValue: { fontSize: '2rem', fontWeight: 700, lineHeight: 1 },
  statLabel: { fontSize: '0.72rem', color: '#7A7090', marginTop: 6, letterSpacing: '0.05em' },
  tabs: { display: 'flex', gap: 4, background: '#12121A', borderRadius: 12, padding: 4, marginBottom: 24 },
  tab: { flex: 1, padding: '10px 8px', borderRadius: 9, border: 'none', background: 'transparent', color: '#7A7090', fontSize: '0.82rem', cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.2s' },
  tabActive: { background: '#16161F', color: '#FFD166', boxShadow: '0 1px 8px rgba(0,0,0,0.3)' },
  section: { background: '#16161F', border: '1px solid rgba(255,209,102,0.12)', borderRadius: 20, padding: 24 },
  sectionTitle: { fontSize: '1rem', fontWeight: 600, color: '#F0EAD6', marginBottom: 20 },
  healthGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12 },
  healthCard: { background: '#0A0A0F', borderRadius: 12, padding: '18px 16px', textAlign: 'center' },
  healthLabel: { fontSize: '0.75rem', color: '#7A7090', marginBottom: 8 },
  healthValue: { fontSize: '1.6rem', fontWeight: 700, color: '#FFD166' },
  empty: { textAlign: 'center', padding: '48px 20px', color: '#7A7090' },
  list: { display: 'flex', flexDirection: 'column', gap: 12 },
  reportCard: { background: '#0A0A0F', borderRadius: 14, padding: '18px 20px', border: '1px solid rgba(255,138,128,0.25)' },
  reportMeta: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, flexWrap: 'wrap' },
  reportEmoji: { fontSize: '1.2rem' },
  reportTo: { fontSize: '0.82rem', color: '#7A7090' },
  reportCount: { fontSize: '0.78rem', background: 'rgba(255,138,128,0.15)', color: '#FF8A80', padding: '2px 8px', borderRadius: 100 },
  reportTime: { fontSize: '0.75rem', color: '#7A7090', marginLeft: 'auto' },
  reportText: { fontStyle: 'italic', color: '#F0EAD6', lineHeight: 1.7, marginBottom: 14, fontSize: '0.95rem' },
  reportActions: { display: 'flex', gap: 8 },
  btnDanger: { padding: '7px 14px', borderRadius: 8, background: 'rgba(255,138,128,0.15)', border: '1px solid rgba(255,138,128,0.3)', color: '#FF8A80', cursor: 'pointer', fontSize: '0.8rem', fontFamily: 'inherit' },
  btnSafe: { padding: '7px 14px', borderRadius: 8, background: 'rgba(185,246,202,0.12)', border: '1px solid rgba(185,246,202,0.25)', color: '#B9F6CA', cursor: 'pointer', fontSize: '0.8rem', fontFamily: 'inherit' },
  userRow: { display: 'flex', alignItems: 'center', gap: 14, padding: '14px 16px', background: '#0A0A0F', borderRadius: 12 },
  userNum: { fontSize: '0.72rem', color: '#7A7090', width: 28, textAlign: 'center' },
  userAvatar: { width: 36, height: 36, borderRadius: '50%', background: 'linear-gradient(135deg, #FFD166, #FF9F1C)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, color: '#0A0A0F', fontSize: '0.9rem', flexShrink: 0 },
  userInfo: { flex: 1, minWidth: 0 },
  userName: { fontSize: '0.92rem', fontWeight: 500, color: '#F0EAD6' },
  userMeta: { fontSize: '0.72rem', color: '#7A7090', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
};
